/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { getCountryCallingCode, type CountryCode } from "libphonenumber-js";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { cssFns } from "@superweb/css";
import { useIsMobile, useTypo, useUiColors } from "@superweb/ui";

import type { UseV1ParkInfoGetResult } from "@fleet/api/fleet-forms";

import { useMessage } from "#internal/intl";
import { Offer, PageHeader } from "#internal/ui";
import { useErrorMessages } from "#internal/utils";
import { useSubmitErrorMessages } from "#internal/utils/validation";

import { DownloadAppPage } from "./download-app-page-deprecated";
import { DriverLicenseDetailForm } from "./driver-license-details-form";
import { RegisterForm } from "./register-form";
import { VehicleDetailsForm } from "./vehicle-details-form";
import { useLogAction } from "../analytics";

export type ParkInfoData = Extract<
  UseV1ParkInfoGetResult,
  {
    status: 200;
  }
>["data"];

type Stage = "main" | "vehicle" | "license" | "download_app";

export const Form = ({
  parkInfo,
  refId,
  isIframe,
  isDelivery,
  country,
}: {
  parkInfo: ParkInfoData;
  refId: string;
  isIframe: boolean;
  isDelivery: boolean;
  country: string;
}) => {
  const isMobile = useIsMobile();
  const uiColors = useUiColors();
  const message = useMessage();
  const errorMessages = useErrorMessages();
  const submitErrorMessages = useSubmitErrorMessages(parkInfo.country_code);
  const logAction = useLogAction("contact");

  const hasVehicleStep = parkInfo.form_requirements.vehicle === "optional";
  const hasLicenseStep = parkInfo.form_requirements.license === "optional";

  const countryCode = getCountryCallingCode(
    parkInfo.country_code as CountryCode,
  );

  const stages = [
    "main",
    ...(hasVehicleStep ? ["vehicle"] : []),
    ...(hasLicenseStep ? ["license"] : []),
  ];

  const [step, setStep] = useState({
    total: stages.length,
    current: 1,
  });
  const [hasSubmit, setSubmit] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState<string>();

  const onError = (code?: string) => {
    if (code) {
      setFormErrorMessage(submitErrorMessages[code] ?? errorMessages.default);
    } else {
      setFormErrorMessage(errorMessages.default);
    }
  };

  const onSuccess = () => {
    setFormErrorMessage(undefined);
  };

  const formErrorContent = formErrorMessage ? (
    <div
      role="alert"
      css={{
        color: uiColors.error,
      }}
    >
      {formErrorMessage}
    </div>
  ) : undefined;

  const {
    content = undefined,
    title = undefined,
    stage = undefined,
  } = {
    main: {
      content: (
        <RegisterForm
          onSubmit={() => {
            if (step.current === step.total) {
              setSubmit(true);
            } else {
              setStep((prev) => ({
                ...prev,
                current: Math.min(prev.total, ++prev.current),
              }));
            }
          }}
          countryCode={countryCode}
          refId={refId}
          onError={onError}
          onSuccess={onSuccess}
          parkInfo={parkInfo}
          bottomSheetContent={formErrorContent}
        />
      ),
      title: message({
        id: "ae168b5e-e7f1-4400-9287-b6655bffcd96",
        context: "Yango forms. Header title",
        default: "Contractor registration",
      }),
      stage: "main",
    },
    vehicle: {
      content: (
        <VehicleDetailsForm
          onSubmit={() => {
            if (step.current === step.total) {
              setSubmit(true);
            } else {
              setStep((prev) => ({
                ...prev,
                current: Math.min(prev.total, ++prev.current),
              }));
            }
          }}
          refId={refId}
          vehicleTypes={parkInfo.vehicle_types}
          onError={onError}
          onSuccess={onSuccess}
          bottomSheetContent={formErrorContent}
        />
      ),
      title: message({
        id: "ae168b5e-e7f1-4400-9287-b6655bffcd95",
        context: "Yango forms. Vehicle details form. Header title",
        default: "Add vehicle details",
      }),
      stage: "vehicle",
    },
    license: {
      content: (
        <DriverLicenseDetailForm
          countryCode={parkInfo.country_code}
          countryId={parkInfo.country_id}
          onSumbit={() => setSubmit(true)}
          refId={refId}
          onError={onError}
          onSuccess={onSuccess}
          licenseTemplatesOld={parkInfo.license_number_templates}
          bottomSheetContent={formErrorContent}
        />
      ),
      title: message({
        id: "0291ef6c-771c-453a-b8fd-f35500860dfd",
        context: "Yango forms. Driver details form. Header title",
        default: "Driver's license details",
      }),
      stage: "license",
    },
  }[stages[step.current - 1] ?? "main"] ?? {};

  const yangoBrand = parkInfo.brand === "yango";

  const hasOffer =
    !isIframe &&
    (parkInfo.promo_text || parkInfo.phone || parkInfo.message_link);

  const successMessage = yangoBrand
    ? message({
        id: "9e5c505a-477d-4811-8e70-3a6699695eae",
        context: "Yango forms. Download app page. Title",
        default: "Registration complete!",
      })
    : message({
        id: "3aa53184-d742-44b0-82aa-c14b64fb5bed",
        context: "Yandex forms. Download app page. Title",
        default: "Request submitted",
      });

  return (
    <>
      <Helmet>
        <title>{hasSubmit ? successMessage : title}</title>
      </Helmet>
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          ...(isMobile
            ? { rowGap: "8px", ...cssFns.padding("10px") }
            : { rowGap: "20px", ...cssFns.padding("16px") }),
          backgroundColor: uiColors.backgroundMinor,
        }}
      >
        {!isIframe && <PageHeader partnerName={parkInfo.partner_name} />}
        <main
          css={{
            display: "grid",
            justifyItems: "center",
            height: `calc(100% - 32px)`,
          }}
        >
          <div
            css={{
              height: "100%",
              display: "flex",
              justifyContent: "start",
              flexDirection: "column",
              rowGap: "16px",
              maxWidth: "764px",
              width: "100%",
            }}
          >
            <div
              css={{
                flexGrow: "1",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                isolation: "isolate",
                boxSizing: "border-box",
                rowGap: "16px",
              }}
            >
              {hasOffer && (
                <Offer
                  offerText={parkInfo.promo_text}
                  isDelivery={isDelivery}
                  onPhonePress={() =>
                    logAction("phone_click", {
                      onlyYM: true,
                      params: {
                        stage: hasSubmit ? "download_app" : (stage as Stage),
                      },
                    })
                  }
                  onMessengerPress={() =>
                    logAction("messenger_click", {
                      onlyYM: true,
                      params: {
                        stage: hasSubmit ? "download_app" : (stage as Stage),
                      },
                    })
                  }
                  phone={parkInfo.phone}
                  messageLink={parkInfo.message_link}
                />
              )}
              <div
                css={{
                  flexGrow: "1",
                  ...(hasSubmit
                    ? {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }
                    : {
                        display: "grid",
                      }),
                  backgroundColor: uiColors.background,
                  ...cssFns.border({ radius: "26px" }),
                }}
              >
                {hasSubmit ? (
                  <DownloadAppPage
                    androidHref={parkInfo.link_to_pro.android_app}
                    iosHref={parkInfo.link_to_pro.ios_app}
                    successMessage={successMessage}
                    isDelivery={isDelivery}
                    country={country}
                  />
                ) : (
                  <div
                    css={{
                      display: "grid",
                      gridTemplateRows: title ? "auto 1fr" : "1fr",
                    }}
                  >
                    {title ? (
                      <FormHeader step={step} title={title} />
                    ) : undefined}
                    {content}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

const FormHeader = ({
  title,
  step,
}: {
  title: string;
  step: { current: number; total: number };
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
      }}
    >
      <div
        css={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "start",
          alignItems: "center",
          ...cssFns.padding("4px"),
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            rowGap: "18px",
            ...cssFns.padding("14px", "12px"),
          }}
        >
          <span
            css={{
              ...typo({
                level: "body1",
                weight: "medium",
                density: "tight",
              }),
              color: uiColors.text,
            }}
          >
            {title}
          </span>
        </div>
      </div>
      {step.total > 1 && (
        <div
          css={{
            marginBlockEnd: "4px",
            ...cssFns.padding("0", "20px"),
          }}
        >
          <StepIndicator {...step} />
        </div>
      )}
    </div>
  );
};

const StepIndicator = ({
  current,
  total,
}: {
  current: number;
  total: number;
}) => {
  const uiColors = useUiColors();

  return (
    <div
      css={{
        width: "calc(100% + 40px)",
        height: "4px",
        display: "grid",
        gridTemplateColumns: `repeat(${total}, 1fr)`,
        ...cssFns.margin("0", "-20px"),
      }}
    >
      {Array.from({ length: total }, (_, index) => index).map((index) => (
        <div
          key={index}
          css={{
            backgroundColor:
              current - 1 === index
                ? uiColors.controlMain
                : uiColors.controlMinor,
          }}
        />
      ))}
    </div>
  );
};
